import React from 'react';
// import { ERROR_TYPES } from '../../validations/validationRulesProduct95';

/**
 * ValidationAlert Component
 * Displays validation errors, warnings, and suggestions in a user-friendly format
 */
function ValidationAlert({ validationResult, className = '' }) {
  if (!validationResult) {
    return null;
  }

  const { errors = [], warnings = [], suggestions = [] } = validationResult;

  return (
    <div className={className}>
      {/* Blocking Errors */}
      {errors.length > 0 && (
        <div className="alert alert-danger" role="alert">
          <div className="d-flex align-items-start mb-2">
            <div className="flex-grow-1">
              <h6 className="alert-heading mb-0">
                <strong>
                  Errori Bloccanti (
                  {errors.length}
                  )
                </strong>
              </h6>
            </div>
          </div>
          <hr className="my-2" />
          <ul className="mb-2" style={{ paddingLeft: '1.5rem' }}>
            {errors.map((error, index) => (
              <li key={index} className="mb-2">
                <strong>
                  {error.code}
                  :
                </strong>
                {' '}
                {error.message}
                {error.details && Object.keys(error.details).length > 0 && (
                  <div className="mt-1">
                    <small className="text-muted">
                      {Object.entries(error.details).map(([key, value]) => (
                        <span key={key} className="me-2">
                          {key}
                          :
                          {JSON.stringify(value)}
                        </span>
                      ))}
                    </small>
                  </div>
                )}
              </li>
            ))}
          </ul>
          <div className="mt-2 p-2" style={{ backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: '4px' }}>
            <small className="d-block text-danger">
              <strong> Azione Richiesta:</strong>
              {' '}
              Correggi questi errori prima di procedere con la quotazione.
            </small>
          </div>
        </div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="alert alert-warning" role="alert">
          <div className="d-flex align-items-start mb-2">
            <div className="flex-grow-1">
              <h6 className="alert-heading mb-0">
                <strong>
                  Avvisi (
                  {warnings.length}
                  )
                </strong>
              </h6>
            </div>
          </div>
          <hr className="my-2" />
          <ul className="mb-2" style={{ paddingLeft: '1.5rem' }}>
            {warnings.map((warning, index) => (
              <li key={index} className="mb-2">
                <strong>
                  {warning.code}
                  :
                </strong>
                {' '}
                {warning.message}
                {warning.details && Object.keys(warning.details).length > 0 && (
                  <div className="mt-1">
                    <small className="text-muted">
                      {Object.entries(warning.details).map(([key, value]) => (
                        <span key={key} className="me-2">
                          {key}
                          :
                          {JSON.stringify(value)}
                        </span>
                      ))}
                    </small>
                  </div>
                )}
              </li>
            ))}
          </ul>
          <div className="mt-2 p-2" style={{ backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: '4px' }}>
            <small className="d-block" style={{ color: '#856404' }}>
              <strong>Informazione:</strong>
              {' '}
              Puoi procedere, ma ti consigliamo di verificare questi avvisi.
            </small>
          </div>
        </div>
      )}

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="alert alert-info" role="alert">
          <div className="d-flex align-items-start mb-2">
            <div className="flex-grow-1">
              <h6 className="alert-heading mb-0">
                <strong>
                  Suggerimenti (
                  {suggestions.length}
                  )
                </strong>
              </h6>
            </div>
          </div>
          <hr className="my-2" />
          <ul className="mb-0" style={{ paddingLeft: '1.5rem' }}>
            {suggestions.map((suggestion, index) => (
              <li key={index} className="mb-2">
                {suggestion.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Success Message */}
      {errors.length === 0 && warnings.length === 0 && suggestions.length === 0 && (
        <div className="alert alert-success" role="alert">
          <div className="d-flex align-items-center">
            <div>
              <strong>Validazione Completata con Successo</strong>
              <p className="mb-0 mt-1">
                Tutte le verifiche sono state superate. Puoi procedere con la quotazione.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * ValidationSummary Component
 * Displays a compact summary of validation status
 */
export function ValidationSummary({ validationResult, className = '' }) {
  if (!validationResult) {
    return null;
  }

  const { errors = [], warnings = [], suggestions = [] } = validationResult;
  const hasErrors = errors.length > 0;
  const hasWarnings = warnings.length > 0;
  const hasSuggestions = suggestions.length > 0;

  if (!hasErrors && !hasWarnings && !hasSuggestions) {
    return (
      <div className={`badge bg-success ${className}`}>
        Validato
      </div>
    );
  }

  return (
    <div className={`d-flex gap-2 ${className}`}>
      {hasErrors && (
        <span className="badge bg-danger">
          {errors.length}
          {' '}
          {errors.length === 1 ? 'Errore' : 'Errori'}
        </span>
      )}
      {hasWarnings && (
        <span className="badge bg-warning text-dark">
          {warnings.length}
          {' '}
          {warnings.length === 1 ? 'Avviso' : 'Avvisi'}
        </span>
      )}
      {hasSuggestions && (
        <span className="badge bg-info text-dark">
          {suggestions.length}
          {' '}
          {suggestions.length === 1 ? 'Suggerimento' : 'Suggerimenti'}
        </span>
      )}
    </div>
  );
}

/**
 * ValidationBadge Component
 * Displays a simple badge indicating validation status
 */
export function ValidationBadge({ validationResult, showText = true, className = '' }) {
  if (!validationResult) {
    return null;
  }

  const hasErrors = validationResult.errors?.length > 0;
  const hasWarnings = validationResult.warnings?.length > 0;

  if (hasErrors) {
    return (
      <span className={`badge bg-danger ${className}`}>
        {showText ? 'Errori di validazione' : ''}
      </span>
    );
  }

  if (hasWarnings) {
    return (
      <span className={`badge bg-warning text-dark ${className}`}>
        {showText ? 'Avvisi' : ''}
      </span>
    );
  }

  return (
    <span className={`badge bg-success ${className}`}>
      {showText ? 'Validato' : ''}
    </span>
  );
}

export default ValidationAlert;
